/**
 * useHelpDrawerState — état, effets et handlers du HelpDrawer (orchestrateur).
 * Extrait tel quel de HelpDrawer.tsx pour maintenir le fichier hub < 300 lignes.
 * ZÉRO changement de comportement : logique identique à l'original.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { type HelpArticle } from '../../hooks/useHelp'
import { useHelpAssistant } from '../../hooks/useHelpAssistant'
import { shouldSuppressAutoFocus } from '../../hooks/use-mobile'
import { useHelpSearchIndex } from '../../hooks/useHelpSearchIndex'
import { useHelpRecents } from '../../hooks/useHelpRecents'
import type { PendingHelpIntent } from '../../contexts/HelpContext'
import type { RecentEntry } from '../../lib/helpSearchHistory'

/** Focus auto desktop/pointeur-fin uniquement (pas de clavier virtuel mobile). */
function isFinePointerDesktop(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.matchMedia('(min-width: 1024px)').matches &&
        window.matchMedia('(pointer: fine)').matches
    )
}

export interface UseHelpDrawerStateParams {
    isOpen: boolean
    articles: HelpArticle[]
    currentArticle: HelpArticle | null
    onShowArticle: (slug: string) => void
    onBackToList: () => void
    isLoading: boolean
    error: string | null
    enableAssistant: boolean
    pendingIntent: PendingHelpIntent | null
    onConsumeIntent?: () => void
    userId?: string
}

export function useHelpDrawerState({
    isOpen,
    articles,
    currentArticle,
    onShowArticle,
    onBackToList,
    isLoading,
    error,
    enableAssistant,
    pendingIntent,
    onConsumeIntent,
    userId,
}: UseHelpDrawerStateParams) {
    const { t } = useTranslation()
    const [searchQuery, setSearchQuery] = useState('')
    const [pendingSection, setPendingSection] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const assistant = useHelpAssistant()
    const [assistantActive, setAssistantActive] = useState(false)
    const recents = useHelpRecents(userId, isOpen, assistant, assistantActive)

    const { items: indexItems, isLoading: indexLoading } = useHelpSearchIndex(isOpen, userId ?? 'anon')
    const contextSlugs = useMemo(() => new Set(articles.map((a) => a.slug)), [articles])

    const inAssistantThread = assistantActive && !currentArticle

    useEffect(() => {
        if (!isOpen) {
            // Réinitialisation à la fermeture.
            setAssistantActive(false)
            assistant.reset()
            setSearchQuery('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // ── §2 : focus auto du champ à l'ouverture (desktop), une fois par session ──
    const focusedRef = useRef(false)
    useEffect(() => {
        if (!isOpen) {
            focusedRef.current = false
            return
        }
        if (focusedRef.current || isLoading || error || inAssistantThread) return
        if (!isFinePointerDesktop()) return // pas de clavier virtuel mobile
        const raf = requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.focus()
                focusedRef.current = true
            }
        })
        return () => cancelAnimationFrame(raf)
    }, [isOpen, isLoading, error, inAssistantThread, currentArticle])

    // Ouvrir un article efface la recherche (flèche retour → liste contextuelle, §3).
    const handleSelectArticle = useCallback(
        (slug: string, section: string | null = null) => {
            setSearchQuery('')
            setPendingSection(section)
            onShowArticle(slug)
        },
        [onShowArticle],
    )

    const handleStartConversation = useCallback(
        (question: string) => {
            const q = question.trim()
            if (!q) return
            onBackToList() // quitte l'article, sinon le fil ne s'affiche pas (§3)
            recents.beginConversation()
            setAssistantActive(true)
            assistant.startNew(q)
        },
        [assistant, recents, onBackToList],
    )

    // Suivi dans le fil : garantit un id (cas « nouvelle conversation » puis question).
    const handleAsk = useCallback(
        (question: string) => {
            recents.ensureConversation()
            assistant.ask(question)
        },
        [assistant, recents],
    )

    // Ouverture d'un article DEPUIS la recherche : mémorise la requête validée (§11).
    const handleOpenFromSearch = useCallback(
        (slug: string) => {
            recents.recordSearchQuery(searchQuery.trim())
            handleSelectArticle(slug, null)
        },
        [searchQuery, recents, handleSelectArticle],
    )

    // « Nouvelle conversation » : vide le fil mais RESTE dans la vue assistant
    // (champ de relance prêt). « Retour à l'aide » : quitte le fil vers la liste.
    const handleNewConversation = useCallback(() => {
        recents.endConversation()
        assistant.reset()
    }, [assistant, recents])

    const handleBackToHelp = useCallback(() => {
        setAssistantActive(false)
        handleNewConversation()
    }, [handleNewConversation])

    // §5 : rouvre un fil archivé TEL QUEL (sans régénérer) ; poursuivable via handleAsk.
    const handleReopenConversation = useCallback(
        (entry: Extract<RecentEntry, { kind: 'conversation' }>) => {
            recents.adoptConversation(entry.id)
            setAssistantActive(true)
            assistant.restore(entry.turns)
        },
        [assistant, recents],
    )

    const handleReplaySearch = useCallback((query: string) => {
        setSearchQuery(query)
        // Pas de focus auto sur mobile/tactile (clavier virtuel masquant — TECH-011).
        if (!shouldSuppressAutoFocus()) requestAnimationFrame(() => inputRef.current?.focus())
    }, [])

    // ── Consommation du slot passerelle (§8.2), clé sur le nonce ─────────────
    useEffect(() => {
        if (!pendingIntent) return
        if (pendingIntent.kind === 'assistant') {
            handleStartConversation(pendingIntent.text)
        } else {
            setSearchQuery(pendingIntent.text)
            // Pas de focus auto sur mobile/tactile (clavier virtuel masquant — TECH-011).
            if (!shouldSuppressAutoFocus()) requestAnimationFrame(() => inputRef.current?.focus())
        }
        onConsumeIntent?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingIntent?.nonce])

    const headerBack = currentArticle
        ? {
              onClick: onBackToList,
              label: assistantActive ? t('help.assistant.backToAnswer') : t('help.backToList'),
          }
        : inAssistantThread
          ? { onClick: handleBackToHelp, label: t('help.assistant.backToHelp') }
          : null

    const headerTitle = currentArticle
        ? currentArticle.title
        : inAssistantThread
          ? t('help.assistant.title')
          : t('help.title')

    const handleOpenDocCenter = () => {
        const url = currentArticle ? `/knowledge/${currentArticle.slug}` : '/knowledge'
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const searchProps = {
        query: searchQuery,
        onQueryChange: setSearchQuery,
        contextualArticles: articles,
        indexItems,
        indexLoading,
        contextSlugs,
        recents: recents.recents,
        enableAssistant,
        onAskAI: handleStartConversation,
        onOpenArticle: handleOpenFromSearch,
        onReplaySearch: handleReplaySearch,
        onReopenConversation: handleReopenConversation,
        onRemoveRecent: recents.removeEntry,
        onClearRecent: recents.clearAll,
        inputRef,
    }

    return {
        assistant,
        inAssistantThread,
        pendingSection,
        handleAsk,
        handleSelectArticle,
        handleNewConversation,
        headerBack,
        headerTitle,
        handleOpenDocCenter,
        searchProps,
    }
}
